import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useIntl } from 'react-intl';
import { AlertBanner } from 'components/ui/atoms';
import { elementEvents, ElementType } from 'helpers/eventing';
import { clearAlert, getAlert, useAppDispatch, useAppSelector } from 'store';
import { getAlertMessage, getAlertType } from './alert.messages';
import { FormattedMessageProps } from './types';

export const Alert = () => {
  const dispatch = useAppDispatch();
  const { code, value } = useAppSelector(getAlert);
  const { formatMessage } = useIntl();

  // Clears the alert when the component is unmounted
  useEffect(
    // cleanup function
    () => () => {
      dispatch(clearAlert());
    },
    [dispatch],
  );

  const alertMessage = code ? getAlertMessage(code, value) : null;

  // Get the text for tagular events, replacing {supportLink} with hardcoded text
  const getTagularText = (): string => {
    if (!alertMessage?.props) {
      return code ?? '';
    }

    const { values, id, defaultMessage } =
      alertMessage.props as FormattedMessageProps;

    // If there's a supportLink in the values, replace it with the url
    const message = formatMessage(
      {
        id,
        defaultMessage,
      },
      {
        ...values,
        supportLink: process.env.NEXT_PUBLIC_EDX_SUPPORT_URL ?? '',
      } as Record<string, string>,
    );
    return String(message);
  };

  const alertTagularElement = {
    elementType: ElementType.Entry,
    name: 'checkout-banner',
    text: getTagularText(),
    htmlId: 'message-banner',
    location: 'above-checkout',
  };

  const { elementViewed } = elementEvents({
    webElement: alertTagularElement,
  });

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && code) {
      elementViewed();
    }
  }, [inView, code, elementViewed]);

  if (!code) {
    return null;
  }

  return (
    <div ref={ref}>
      <AlertBanner
        variant={getAlertType(code)}
        onClose={() => dispatch(clearAlert())}
      >
        {alertMessage}
      </AlertBanner>
    </div>
  );
};
